/**
 * Regjistri i Aseteve — faqja `/assets`.
 *
 * Nuk është "tabela e pajisjeve". Është regjistri ku një pajisje **dorëzohet**,
 * **kthehet** dhe **e mban gjurmën** e të dyjave: prandaj kolona qendrore nuk
 * është modeli, por KUSH E MBAN, dhe veprimet e rreshtit janë dorëzimi e kthimi,
 * jo redaktimi.
 *
 * Statusi nuk redaktohet nga tabela. Ai vjen nga cikli — dhe cikli kalon nëpër
 * serverin, që e refuzon çdo status që bie ndesh me caktimet e hapura.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Paper, Card, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TablePagination, TextField, MenuItem,
  Button, Stack, IconButton, Tooltip, Alert, InputAdornment, Chip, CircularProgress,
  Tabs, Tab, alpha, Menu,
} from '@mui/material';
import AddIcon                from '@mui/icons-material/Add';
import SearchIcon             from '@mui/icons-material/Search';
import EditOutlinedIcon       from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon      from '@mui/icons-material/DeleteOutline';
import HistoryIcon            from '@mui/icons-material/History';
import AssignmentIndIcon      from '@mui/icons-material/AssignmentInd';
import AssignmentReturnIcon   from '@mui/icons-material/AssignmentReturn';
import Inventory2Icon         from '@mui/icons-material/Inventory2';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import UploadFileIcon       from '@mui/icons-material/UploadFile';
import BuildOutlinedIcon       from '@mui/icons-material/BuildOutlined';
import PersonOutlineIcon       from '@mui/icons-material/PersonOutline';
import ErrorOutlineIcon        from '@mui/icons-material/ErrorOutline';

import { ReportPage, ExportMenu } from '../report/ReportKit';
import StatusChip      from '../common/StatusChip';
import Toast           from '../actions/Toasts';
import usePermissions  from '../../hooks/usePermissions';
import useConfirm      from '../../hooks/useConfirm';
import apiService      from '../../services/apiServices';
import assetsService   from '../../services/assetsService';
import normalizeWorker from '../../services/workerNormalizer';
import {
  CATEGORIES, STATUSES, categoryMeta, categoryLabel, statusChip, statusLabel,
  conditionChip, conditionLabel, matchesQuery, summarize, money, dateText,
  assetTitle, canAssign, canReturn, warrantyState,
} from '../../config/assets';
import { printAssets, downloadAssetsCsv } from '../../utils/assetsExport';

import AssignAssetDialog   from './AssignAssetDialog';
import AssetsImportDialog  from './AssetsImportDialog';
import ReturnAssetDialog   from './ReturnAssetDialog';
import AssetHistoryDialog  from './AssetHistoryDialog';
import AuthImage           from './AuthImage';

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Shifrat e kokës. Rendi është rendi i ciklit — gjithsej, te punonjësit, e lirë,
 * në servis, jashtë loje — dhe vlera në fund, sepse ajo është shifra e
 * kontabilitetit, jo e operacionit.
 */
const KPIS = [
  { key: 'total',     label: 'Gjithsej asete', Icon: Inventory2Icon,   tone: 'primary' },
  { key: 'assigned',  label: 'Të caktuara',    Icon: PersonOutlineIcon, tone: 'info' },
  { key: 'available', label: 'Në dispozicion', Icon: Inventory2Icon,   tone: 'success' },
  { key: 'service',   label: 'Në servis',      Icon: BuildOutlinedIcon, tone: 'warning' },
  { key: 'lost',      label: 'Të humbura',     Icon: ErrorOutlineIcon,  tone: 'error' },
  { key: 'value',     label: 'Vlera e blerjes', Icon: DescriptionOutlinedIcon, tone: 'primary', money: true },
];

export default function AssetsRegister() {
  const [assets,  setAssets]  = useState([]);
  const [workers, setWorkers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Filtrat
  const [search, setSearch]     = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus]     = useState('all');

  // Renditja & faqosja
  const [orderBy, setOrderBy] = useState('assetCode');
  const [order,   setOrder]   = useState('asc');
  const [page,    setPage]    = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Dialogët
  const [assignFor,   setAssignFor]   = useState(null);
  const [returnFor,   setReturnFor]   = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [detail,      setDetail]      = useState(null);
  const [detailBusy,  setDetailBusy]  = useState(false);
  const [detailError, setDetailError] = useState('');

  const [docMenu, setDocMenu] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const say = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const { can } = usePermissions();
  const canCreate = can('assets.create') || can('assets.manage');
  const canEdit   = can('assets.edit')   || can('assets.manage');
  const canManage = can('assets.manage');
  const canDelete = can('assets.delete');
  const [confirm, confirmUI] = useConfirm();

  // ── Ngarkimi ───────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setLoadError('');
    try {
      const rows = await assetsService.list();
      setAssets(Array.isArray(rows) ? rows : []);
    } catch (e) {
      // Gabimi thuhet. Një listë bosh mbi një server të rënë do të lexohej si
      // "kompania nuk ka asnjë pajisje" — përgjigje e rreme për një regjistër.
      setLoadError(e.message || 'Regjistri nuk u ngarkua. Kontrollo lidhjen me serverin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [wRes, sRes, dRes] = await Promise.all([
          apiService().get('workers'),
          apiService().get('settings'),
          // Departamentet i duhen vetëm importit — për të lidhur emrin e shkruar
          // te fleta me id-në te sistemi. Dështimi i tyre nuk e prish faqen.
          apiService().get('departments').catch(() => []),
        ]);
        if (!alive) return;
        const arr = (r) => { const raw = r?.data || r; return Array.isArray(raw) ? raw : []; };
        setWorkers(arr(wRes).map((w, i) => normalizeWorker(w, i)));
        setCompany(arr(sRes)[0] || {});
        setDepartments(arr(dRes));
      } catch (_) { /* lista e punëtorëve mungon vetëm te dialogu i dorëzimit */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => { setPage(0); }, [search, category, status]);

  // ── Filtrimi ───────────────────────────────────────────────────────────────
  const data = useMemo(() => assets.filter((a) => {
    if (!matchesQuery(a, search))                  return false;
    if (category !== 'all' && a.category !== category) return false;
    if (status   !== 'all' && a.status   !== status)   return false;
    return true;
  }), [assets, search, category, status]);

  const sorted = useMemo(() => {
    const dir = order === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      if (orderBy === 'purchaseValue') return dir * ((a.purchaseValue || 0) - (b.purchaseValue || 0));
      if (orderBy === 'holder')  return dir * String(a.holder?.name || '').localeCompare(String(b.holder?.name || ''), 'sq');
      if (orderBy === 'status')  return dir * statusLabel(a.status).localeCompare(statusLabel(b.status), 'sq');
      return dir * String(a[orderBy] ?? '').localeCompare(String(b[orderBy] ?? ''), 'sq');
    });
  }, [data, order, orderBy]);

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const stats = useMemo(() => summarize(data), [data]);
  const today = todayISO();

  function handleSort(col) {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(col);
  }

  const dirty = search || category !== 'all' || status !== 'all';
  const filterLabels = [
    category !== 'all' && `Kategoria: ${categoryLabel(category)}`,
    status   !== 'all' && `Statusi: ${statusLabel(status)}`,
    search   && `Kërkim: ${search}`,
  ].filter(Boolean);

  // ── Veprimet ───────────────────────────────────────────────────────────────
  async function doAssign(body) {
    const res = await assetsService.assign(assignFor.id, body);
    setAssets((rows) => rows.map((r) => (r.id === res.asset.id ? res.asset : r)));
    setAssignFor(null);
    say(`${res.asset.assetCode} iu dorëzua punonjësit.`);
  }

  async function doReturn(body) {
    const res = await assetsService.ret(returnFor.asset.id, body);
    setAssets((rows) => rows.map((r) => (r.id === res.asset.id ? res.asset : r)));
    setReturnFor(null);
    say(res.deductionId
      ? `Kthimi u regjistrua. Ndalesa #${res.deductionId} pret aprovim te Rregullimet e pagës.`
      : 'Kthimi u regjistrua.');
  }

  async function openHistory(asset) {
    setHistoryOpen(true); setDetailBusy(true); setDetailError(''); setDetail(null);
    try {
      setDetail(await assetsService.detail(asset.id));
    } catch (e) {
      setDetailError(e.message || 'Historiku nuk u ngarkua.');
    } finally {
      setDetailBusy(false);
    }
  }

  async function openReturn(asset) {
    // Caktimi i hapur vjen nga serveri, jo nga rreshti: rreshti mban vetëm emrin
    // e mbajtësit, kurse dialogu i duhen gjendja në dorëzim dhe pajisjet shtesë.
    try {
      const d = await assetsService.detail(asset.id);
      const open = (d.assignments || []).find((x) => x.open) || null;
      setReturnFor({ asset: d.asset, assignment: open });
    } catch (e) {
      say(e.message || 'Të dhënat e dorëzimit nuk u lexuan.', 'error');
    }
  }

  async function removeAsset(asset) {
    const okToDelete = await confirm({
      title: 'Fshi asetin',
      message: `Fshi ${asset.assetCode} — ${asset.name}?`,
      details: 'Vetëm asetet pa histori dorëzimesh mund të fshihen.',
      confirmLabel: 'Fshi',
    });
    if (!okToDelete) return;
    try {
      await assetsService.remove(asset.id);
      setAssets((rows) => rows.filter((r) => r.id !== asset.id));
      say('Aseti u fshi.');
    } catch (e) {
      say(e.message || 'Fshirja dështoi.', 'error');
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <ReportPage sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* Koka e faqes: ikona, titulli, veprimet */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap gap={1}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'primary.soft', color: 'primary.main',
                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Inventory2Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Regjistri i Aseteve</Typography>
            <Typography variant="caption" color="text.secondary">
              Pajisjet e kompanisë, kush i mban dhe si u kthyen
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <ExportMenu
            onCsv={() => downloadAssetsCsv(sorted)}
            onPdf={() => printAssets(sorted, { company: company.CompanyName || company.Name || '', filters: filterLabels })}
            onPrint={() => printAssets(sorted, { company: company.CompanyName || company.Name || '', filters: filterLabels })}
            disabled={!sorted.length}
          />
          {canManage && (
            <Button variant="outlined" size="small" startIcon={<UploadFileIcon />}
              onClick={() => setImportOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}>
              Importo
            </Button>
          )}
          {canManage && (
            <>
              <Button variant="outlined" size="small" startIcon={<DescriptionOutlinedIcon />}
                onClick={(e) => setDocMenu(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}>
                Procesverbal
              </Button>
              <Menu anchorEl={docMenu} open={!!docMenu} onClose={() => setDocMenu(null)}>
                <MenuItem component={Link} to="/assets/handover/new" onClick={() => setDocMenu(null)}>
                  Dorëzim te punonjësi
                </MenuItem>
                <MenuItem component={Link} to="/assets/handover/new?kind=return" onClick={() => setDocMenu(null)}>
                  Kthim nga punonjësi
                </MenuItem>
              </Menu>
            </>
          )}
          {canCreate && (
            <Button variant="contained" size="small" startIcon={<AddIcon />}
              component={Link} to="/assets/new"
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}>
              Shto aset të ri
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Shifrat e regjistrit. Përqindja rri poshtë numrit sepse "96" nuk thotë
          asgjë pa "nga 128" — dhe pikërisht raporti është ai që lexohet. */}
      <Box sx={{ display: 'grid', gap: 1,
                 gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' } }}>
        {KPIS.map((k) => {
          const value = k.money ? money(stats[k.key]) : stats[k.key];
          const pct = k.key !== 'total' && !k.money && stats.total
            ? `${Math.round((stats[k.key] / stats.total) * 100)}% e totalit` : null;
          return (
            <Paper key={k.key} variant="outlined" sx={{ px: 1.25, py: 1, borderRadius: 2, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 34, height: 34, borderRadius: 1.75, flexShrink: 0,
                           display: 'flex', alignItems: 'center', justifyContent: 'center',
                           bgcolor: (th) => alpha(th.palette[k.tone].main, 0.12),
                           color: `${k.tone}.main` }}>
                  <k.Icon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.05em',
                                           textTransform: 'uppercase', color: 'text.secondary' }}>
                    {k.label}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.25,
                                           fontVariantNumeric: 'tabular-nums' }}>
                    {loading ? '—' : value}
                  </Typography>
                  {pct && <Typography noWrap sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>{pct}</Typography>}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Box>

      {/* Tabet e statusit janë filtri kryesor: ai është edhe pyetja e parë e
          kujtdo — "çka është e lirë?" — ndaj nuk fshihet brenda një liste. */}
      <Tabs value={status} onChange={(_, v) => setStatus(v)} variant="scrollable" scrollButtons="auto"
        sx={{ minHeight: 38, borderBottom: '1px solid', borderColor: 'divider',
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', minHeight: 38 } }}>
        <Tab value="all" label={`Të gjitha (${assets.length})`} />
        {STATUSES.map((st) => (
          <Tab key={st.key} value={st.key}
            label={`${st.label} (${assets.filter((a) => a.status === st.key).length})`} />
        ))}
      </Tabs>

      {/* Filtrat */}
      <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small" placeholder="Kërko kod, emër, seri ose mbajtës…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} /></InputAdornment> }}
          sx={{ width: 260 }}
        />
        <TextField select size="small" value={category} onChange={(e) => setCategory(e.target.value)}
          SelectProps={{ renderValue: (v) => (v === 'all' ? 'Kategoria' : categoryLabel(v)) }} sx={{ width: 165 }}>
          <MenuItem value="all">Të gjitha</MenuItem>
          {CATEGORIES.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
        </TextField>
        {dirty && (
          <Chip size="small" label="Pastro" onDelete={() => { setSearch(''); setCategory('all'); setStatus('all'); }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.disabled">{data.length} nga {assets.length}</Typography>
      </Paper>

      {/* Tabela */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>
                  <TableSortLabel active={orderBy === 'assetCode'} direction={orderBy === 'assetCode' ? order : 'asc'}
                    onClick={() => handleSort('assetCode')}>Kodi</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>
                  <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleSort('name')}>Aseti</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 130, display: { xs: 'none', md: 'table-cell' } }}>Nr. serik</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>
                  <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleSort('status')}>Statusi</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 120, display: { xs: 'none', sm: 'table-cell' } }}>Gjendja</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 170 }}>
                  <TableSortLabel active={orderBy === 'holder'} direction={orderBy === 'holder' ? order : 'asc'}
                    onClick={() => handleSort('holder')}>Mbajtësi</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 110, display: { xs: 'none', lg: 'table-cell' } }}>Garancioni</TableCell>
                <TableCell className="num" sx={{ fontWeight: 700, minWidth: 110, display: { xs: 'none', md: 'table-cell' } }}>
                  <TableSortLabel active={orderBy === 'purchaseValue'} direction={orderBy === 'purchaseValue' ? order : 'asc'}
                    onClick={() => handleSort('purchaseValue')}>Vlera</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 150 }} align="right">Veprimet</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Tri gjendje të ndara: ngarkim, gabim, zbrazëti. Të treja jepnin
                  dikur të njëjtën fjali te modulet e tjera — dhe një server i
                  rënë dukej si një regjistër bosh. */}
              {loading && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {/* `!!` nuk është zbukurim: `loadError` është varg, dhe `'' && …`
                  jep `''` — një nyje teksti brenda <tbody>, të cilën React e
                  raporton si "whitespace text node". */}
              {!loading && !!loadError && (
                <TableRow><TableCell colSpan={9} sx={{ py: 3 }}><Alert severity="error">{loadError}</Alert></TableCell></TableRow>
              )}
              {!loading && !loadError && paged.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {assets.length === 0
                      ? 'Regjistri është bosh — shto asetin e parë.'
                      : 'Asnjë aset nuk përputhet me këta filtra.'}
                  </TableCell>
                </TableRow>
              )}

              {!loading && !loadError && paged.map((a) => {
                const Cat = categoryMeta(a.category).Icon;
                const warranty = warrantyState(a.warrantyUntil, today);
                return (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      <Box component={Link} to={`/assets/${a.id}`}
                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        {a.assetCode}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AuthImage fileId={a.photoFileId} alt={a.name} radius={1}
                          sx={{ width: 34, height: 26, flexShrink: 0 }} fallbackIcon={false} />
                        {/* Kategoria dallohet me ikonë, jo me ngjyrë: nëntë hue-e do
                            ta bënin tabelën ylber dhe ngjyrën pa kuptim. */}
                        <Cat sx={{ fontSize: 18, color: 'text.disabled' }} titleAccess={categoryLabel(a.category)} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }} noWrap>{assetTitle(a)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {categoryLabel(a.category)}{a.location ? ` · ${a.location}` : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontVariantNumeric: 'tabular-nums' }}>
                      {a.serialNumber || '—'}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={statusChip(a.status)} label={statusLabel(a.status)} size="sm" />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <StatusChip status={conditionChip(a.condition)} label={conditionLabel(a.condition)} size="sm" />
                    </TableCell>
                    <TableCell>
                      {a.holder ? (
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }} noWrap>
                            {a.holder.name || 'Punëtor i fshirë'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {a.holder.position || `prej ${dateText(a.holder.since)}`}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {warranty === 'none' ? (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      ) : (
                        <Typography variant="body2"
                          sx={{ color: warranty === 'expired' ? 'text.secondary' : 'text.primary',
                                textDecoration: warranty === 'expired' ? 'line-through' : 'none' }}>
                          {dateText(a.warrantyUntil)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className="num" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {money(a.purchaseValue, a.currency)}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        {canManage && canAssign(a) && (
                          <Tooltip title="Dorëzo te punonjësi" arrow>
                            <IconButton size="small" onClick={() => setAssignFor(a)}>
                              <AssignmentIndIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canManage && canReturn(a) && (
                          <Tooltip title="Regjistro kthimin" arrow>
                            <IconButton size="small" color="primary" onClick={() => openReturn(a)}>
                              <AssignmentReturnIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Historiku" arrow>
                          <IconButton size="small" onClick={() => openHistory(a)}>
                            <HistoryIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        {canEdit && (
                          <Tooltip title="Ndrysho" arrow>
                            <IconButton size="small" component={Link} to={`/assets/${a.id}/edit`}>
                              <EditOutlinedIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Fshi" arrow>
                            <IconButton size="small" color="error" onClick={() => removeAsset(a)}>
                              <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 15, 25, 50]}
          labelRowsPerPage="Rreshta:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
        />
      </Card>

      <AssignAssetDialog
        open={!!assignFor}
        asset={assignFor}
        workers={workers}
        today={today}
        onClose={() => setAssignFor(null)}
        onAssign={doAssign}
      />
      <ReturnAssetDialog
        open={!!returnFor}
        asset={returnFor?.asset}
        assignment={returnFor?.assignment}
        today={today}
        onClose={() => setReturnFor(null)}
        onReturn={doReturn}
      />
      <AssetsImportDialog
        open={importOpen}
        assets={assets}
        workers={workers}
        departments={departments}
        company={company.CompanyName || company.Name || ''}
        onClose={() => setImportOpen(false)}
        onImported={(res) => {
          // Regjistri rilexohet nga serveri, jo shtohet lokalisht: importi
          // krijon edhe caktime, dhe mbajtësi i secilës pajisje vjen nga një
          // JOIN që klienti nuk e bën dot vetë.
          load();
          say(`U importuan ${res?.created || 0} asete`
            + (res?.assigned ? `, ${res.assigned} prej tyre te punonjësit.` : '.')
            + (res?.failed ? ` ${res.failed} rreshta dështuan.` : ''),
            res?.created ? (res?.failed ? 'warning' : 'success') : 'error');
        }}
      />
      <AssetHistoryDialog
        open={historyOpen}
        loading={detailBusy}
        error={detailError}
        detail={detail}
        onClose={() => { setHistoryOpen(false); setDetail(null); }}
      />

      {confirmUI}
      <Toast open={snack.open} message={snack.msg} severity={snack.sev}
        onClose={() => setSnack((s) => ({ ...s, open: false }))} />
    </ReportPage>
  );
}
